// E³-Survey Web結果画面のチャート部品（自前SVG。プロトタイプ v3.0〜v3.2 の lineChart / typeQuadPx / areaColumns /
// priorityMatrix / factBarsArea を React に移植）
// 色はすべてデザイントークン（var(--…)）。チャートは最大2色＋グレー（結果UIパーツ集の決めごと）。
import React, { useEffect, useRef, useState } from 'react';
import E3Icon from './E3Icon';
import {
  AREAS,
  CATS,
  CAT_QS,
  CAT_DEF_SHORT,
  CORR_TH,
  EMP_ITEM_LABEL,
  FACT_CATS,
  FACT_ITEM_NAME,
  FQ,
  TYPE_COLOR,
  TYPE_TH,
  RANK_RANGE,
  fqOf,
  fx1,
} from './e3Const';

const TNUM = { fontVariantNumeric: 'tabular-nums' };

// デルタ（色＝良し悪し・矢印＝向き。本サーベイは全指標「高いほど良い」）
export const Delta = ({ diff, label }) => {
  if (diff == null || isNaN(diff)) return <span className="delta flat">— 変化なし</span>;
  const flat = Math.abs(diff) < 0.05;
  const cls = flat ? 'flat' : diff > 0 ? 'good' : 'bad';
  return (
    <span className={`delta ${cls}`}>
      {!flat && <E3Icon name={diff > 0 ? 'up' : 'down'} />}
      {label ? `${label} ` : ''}
      {diff > 0 ? '+' : ''}
      {diff.toFixed(1)}
    </span>
  );
};

export const RankLegend = () => (
  <div className="rk-legend">
    <span>色＝ランク</span>
    {Object.keys(RANK_RANGE).map((k) => (
      <i key={k} className={`rk-${k.toLowerCase()}`}>
        {k}
      </i>
    ))}
  </div>
);

export const RankBadge = ({ g, label }) =>
  g ? (
    <span className={`as-badge rk-tag rk-${g.toLowerCase()}`}>
      {label ? `${label} ` : ''}
      {g}
    </span>
  ) : null;

// ---- 折れ線（主系列＝オレンジ／比較＝グレー）。labels: 横軸の見出し、series: [{label,color,main,dash,vals:[...]}]
export const LineChart = ({ labels, series, h = 230, w = 640 }) => {
  const vals = series.flatMap((s) => s.vals.filter((v) => v != null));
  if (!vals.length) return <p className="as-hint">表示できるデータがありません。</p>;
  const H = h;
  const W = w;
  const XR = W - 40;
  const lo = Math.max(0, Math.floor(Math.min(...vals) / 10) * 10 - 5);
  const hi = Math.min(100, Math.ceil(Math.max(...vals) / 10) * 10 + 5);
  const X = (i) => 60 + i * ((XR - 60) / Math.max(labels.length - 1, 1));
  const Y = (v) => H - 28 - ((v - lo) / (hi - lo)) * (H - 50);
  const grid = [];
  for (let g = Math.ceil(lo / 10) * 10; g <= hi; g += 10) grid.push(g);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label="推移グラフ">
      {grid.map((g) => (
        <g key={g}>
          <line x1="52" x2={W - 20} y1={Y(g)} y2={Y(g)} stroke="var(--grey-30)" strokeDasharray="3 3" />
          <text x="46" y={Y(g) + 4} textAnchor="end" fontSize="12" fill="var(--grey-45)" style={TNUM}>
            {g}
          </text>
        </g>
      ))}
      <line x1="52" x2="52" y1={Y(hi)} y2={Y(lo)} stroke="var(--grey-45)" />
      {labels.map((l, i) => (
        <text key={i} x={X(i)} y={H - 8} textAnchor="middle" fontSize="12" fill="var(--grey-65)">
          {l}
        </text>
      ))}
      {series.map((s, si) => {
        const pts = s.vals.map((v, i) => [i, v]).filter((p) => p[1] != null);
        return (
          <g key={si}>
            <polyline
              points={pts.map((p) => `${X(p[0])},${Y(p[1])}`).join(' ')}
              fill="none"
              stroke={s.color}
              strokeWidth={s.main ? 3 : 2}
              strokeDasharray={s.dash ? '5 4' : undefined}
            />
            {pts.map((p) => (
              <circle key={p[0]} cx={X(p[0])} cy={Y(p[1])} r={s.main ? 4 : 3} fill={s.color} />
            ))}
          </g>
        );
      })}
    </svg>
  );
};

// ---- 枠の大きさを測って、その px のまま図を描く（SVG を縮めて表示すると文字が 12px より小さくなるため。改修希望 E3-05）。
//   children(size) が図を返す。fill を付けると高さも枠に合わせる（CSS の .tq-fill）。付けないときは幅だけ測る
export const Measure = ({ className, fill, children }) => {
  const ref = useRef(null);
  const [size, setSize] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => {
      const w = Math.round(el.clientWidth);
      const h = Math.round(el.clientHeight);
      if (w <= 0 || (fill && h <= 0)) return;
      setSize((s) => (!s || s.w !== w || s.h !== h ? { w, h } : s));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fill]);
  return (
    <div className={`px-draw ${className || ''}`} ref={ref}>
      {size && children(size)}
    </div>
  );
};

// ---- エンゲージメントタイプの分布図（個人PDF と同じ：横軸＝組織共感／縦軸＝職務共感・各軸 65 で区切る）
//   目盛りは PDF と同じく 0〜65 を半分、65〜100 を残り半分に引き伸ばす（十字の中心＝65）。
//   pts   : 回答者の点 [[職務,組織],...]（サーバーの scatter の並び）
//   marks : 当組織の平均 [{org,job}]
//   size  : { w, h }（Measure で測った枠の大きさ。px のまま描く）
//   ・4 つの区画はタイプの色（TYPE_COLOR＝PDF と同じ色）で薄く塗り、タイプ名は図の四隅の内側に置く（改修希望 E3-08 の 17）
//   ・回答者＝白い丸にグレーの枠線、当組織の平均＝オレンジの二重丸（同上）
//   ・軸は、図の外に沿って「低い ―― 軸の名前（太字）―→ 高い」の形で示す（改修希望 E3-08 の 17・プロトタイプ v3.2 の typeQuadPx。
//     2026-10-16 にこの形へ変更。それまでは 10/5 に決めた「十字の線の端に軸の名前」の形）。
//     横軸（組織共感）は図の下、縦軸（職務共感）は図の左（名前は縦書き、上が高い）。矢印の先が「高い」。目盛りは 0・65・100
// 軸の帯：低い ―― 名前 ―→ 高い（横＝図の下、縦＝図の左）。gap＝名前の左右の線を空ける幅（プロトタイプ v3.2 の値）
const AxisBand = ({ x0, x1, yT, yB, by, xName, yName, gap = 36 }) => {
  const FS = 12;
  const sx = 14;
  const AC = 'var(--grey-45)';
  const xm = (x0 + x1) / 2;
  const ym = (yT + yB) / 2;
  const end = (x, y, a, t) => (
    <text x={x} y={y} textAnchor={a} fontSize={FS} fill="var(--grey-65)">
      {t}
    </text>
  );
  return (
    <g>
      {end(x0, by, 'start', '低い')}
      {end(x1, by, 'end', '高い')}
      <text x={xm} y={by} textAnchor="middle" fontSize="13.5" fontWeight="700" fill="var(--black)">
        {xName}
      </text>
      <line x1={x0 + 32} x2={xm - gap} y1={by - 4} y2={by - 4} stroke={AC} strokeWidth="1" />
      <line x1={xm + gap} x2={x1 - 38} y1={by - 4} y2={by - 4} stroke={AC} strokeWidth="1" />
      <path d={`M${x1 - 31} ${by - 4}l-8 -4v8z`} fill={AC} />
      {end(sx, yB, 'middle', '低い')}
      {end(sx, yT + 10, 'middle', '高い')}
      <text x={sx} y={ym} textAnchor="middle" fontSize="13.5" fontWeight="700" fill="var(--black)" style={{ writingMode: 'vertical-rl' }}>
        {yName}
      </text>
      <line x1={sx} x2={sx} y1={yB - 18} y2={ym + gap} stroke={AC} strokeWidth="1" />
      <line x1={sx} x2={sx} y1={ym - gap} y2={yT + 25} stroke={AC} strokeWidth="1" />
      <path d={`M${sx} ${yT + 18}l-4 8h8z`} fill={AC} />
    </g>
  );
};
const typeScale = (v) => (v <= TYPE_TH ? (v / TYPE_TH) * 0.5 : 0.5 + ((v - TYPE_TH) / (100 - TYPE_TH)) * 0.5);
export const TypeQuad = ({ pts = [], marks = [], size }) => {
  const W = size.w;
  const H = size.h;
  const FS = 12;
  const L = 66; // 縦軸の帯＋目盛り
  const R = 20; // 目盛りの「100」が切れない幅
  const T = 10;
  const B = 52; // 横軸の目盛り＋帯
  const X = (v) => L + typeScale(v) * (W - L - R);
  const Y = (v) => H - B - typeScale(v) * (H - B - T);
  const cx = X(TYPE_TH);
  const cy = Y(TYPE_TH);
  const quads = {
    じっくり集中: [X(0), Y(100), cx - X(0), cy - Y(100)],
    いきいき活躍: [cx, Y(100), X(100) - cx, cy - Y(100)],
    ひとやすみ充電: [X(0), cy, cx - X(0), Y(0) - cy],
    つながり重視: [cx, cy, X(100) - cx, Y(0) - cy],
  };
  const corners = [
    ['じっくり集中', X(0) + 8, Y(100) + 18, 'start'],
    ['いきいき活躍', X(100) - 8, Y(100) + 18, 'end'],
    ['ひとやすみ充電', X(0) + 8, Y(0) - 9, 'start'],
    ['つながり重視', X(100) - 8, Y(0) - 9, 'end'],
  ];
  return (
    <svg
      className="tq"
      viewBox={`0 0 ${W} ${H}`}
      width={W}
      height={H}
      role="img"
      aria-label="組織共感（横）と職務共感（縦）によるエンゲージメントタイプの分布"
    >
      <rect x={X(0)} y={Y(100)} width={X(100) - X(0)} height={Y(0) - Y(100)} fill="var(--white)" stroke="var(--grey-30)" />
      {Object.entries(quads).map(([t, q]) => (
        <rect key={t} x={q[0]} y={q[1]} width={q[2]} height={q[3]} fill={TYPE_COLOR[t]} fillOpacity=".45" />
      ))}
      {[0, TYPE_TH, 100].map((g) => (
        <g key={g}>
          <text x={L - 6} y={Y(g) + 4} textAnchor="end" fontSize={FS} fill="var(--grey-45)" style={TNUM}>
            {g}
          </text>
          <text x={X(g)} y={H - B + 15} textAnchor="middle" fontSize={FS} fill="var(--grey-45)" style={TNUM}>
            {g}
          </text>
        </g>
      ))}
      {pts.map((p, i) => (
        <circle key={i} cx={X(p[1])} cy={Y(p[0])} r="4" fill="var(--white)" stroke="var(--grey-65)" strokeWidth="1.2" />
      ))}
      <line x1={cx} x2={cx} y1={Y(100)} y2={Y(0)} stroke="var(--grey-65)" strokeWidth="1.2" />
      <line x1={X(0)} x2={X(100)} y1={cy} y2={cy} stroke="var(--grey-65)" strokeWidth="1.2" />
      {corners.map(([t, tx, ty, an]) => (
        <text
          key={t}
          x={tx}
          y={ty}
          textAnchor={an}
          fontSize={FS}
          fontWeight="700"
          fill="var(--grey-65)"
          stroke="var(--white)"
          strokeWidth="3"
          paintOrder="stroke"
        >
          {t}
        </text>
      ))}
      {marks
        .filter((m) => m.org != null && m.job != null)
        .map((m, i) => (
          <g key={i}>
            <circle cx={X(m.org)} cy={Y(m.job)} r="8" fill="var(--orange-default)" stroke="var(--white)" strokeWidth="3" />
            <circle cx={X(m.org)} cy={Y(m.job)} r="10.5" fill="none" stroke="var(--orange-default)" strokeWidth="1.5" />
          </g>
        ))}
      <AxisBand x0={X(0)} x1={X(100)} yT={Y(100)} yB={Y(0)} by={H - 9} xName="組織共感" yName="職務共感" />
    </svg>
  );
};

const LEGEND = {
  orgAvg: ['me', '当組織の平均'],
  dots: ['dot', '回答者'],
};
export const TypeLegend = ({ kinds }) => (
  <div className="as-legend tq-legend">
    {kinds.map((k) => (
      <span key={k} className="lg">
        <i className={`mk ${LEGEND[k][0]}`} />
        {LEGEND[k][1]}
      </span>
    ))}
  </div>
);

// ---- 影響度（相関）を 1 つも計算できないときの理由。計算できるときは null。
//   サーバーは回答者 3 名未満、または値がみな同じ（ばらつき 0）のとき相関を出さない（e3Aggregate.js の pearson）。
//   ※PDF はこの場合も 0.00 として描くが、Web は「計算できない」と表示する。
export const corrUnavailable = (d) => {
  if (FACT_CATS.some((c) => d.corr && d.corr[c.key] != null)) return null;
  return d.n < 3
    ? '回答者が3名未満のため、影響度（相関）を計算できません。'
    : '回答にばらつきがないため、影響度（相関）を計算できません。';
};

// ---- 領域ごとにまとめた縦棒グラフ（改修希望 E3-08 の 12・E3-09）
//   「事実項目ごとのスコア」（事実の中項目 20）・「共感項目ごとのスコア」（共感の小項目 20）・「事実項目【領域】ごとのスコア」（小項目 25）で共通。
//   entries: [{ id, name, label, area, v, g }]  label は「|」で折り返してよい位置を示した名前、v＝スコア、g＝グループ全体の平均（無ければ null）
//   ・縦軸：下端＝最低スコア−10 を 10 の位で切り下げ、上端＝最高スコア＋10 を 10 の位で四捨五入（下端は 0 ではない）
//   ・上位 3（強み）＝緑、下位 3（弱み）＝赤、ほか＝グレー。スコアの数字は棒の中
//   ・g があれば点線の折れ線で重ねる（まとまりごとに区切る）。幅が足りないときは項目名を縦書き
//   series を渡すと、棒の代わりに折れ線（要因分析「エンゲージメントタイプごとの事実項目スコア」・E3-10 の 6）
export const FACT_CHART_H = 261;
export const AreaColumns = ({ entries, w, aria, groups, series }) => {
  const vals = (series ? series.flatMap((s) => entries.map((e) => s.vals[e.id])) : entries.map((x) => x.v)).filter(
    (v) => v != null
  );
  if (!vals.length) return <p className="as-hint">表示できるデータがありません。</p>;
  const all = vals.concat(entries.map((x) => x.g).filter((v) => v != null));
  const W = Math.max(w, 320);
  const FS = 12;
  const LH = 15;
  const L = 34;
  const R = 8;
  const T = 20;
  const slot = (W - L - R) / entries.length;
  const maxCh = Math.floor((slot - 4) / FS);
  const horiz = maxCh >= 4;
  const chop = (seg) => {
    if (seg.length <= maxCh + 1) return [seg];
    const n = Math.ceil(seg.length / maxCh);
    const k = Math.ceil(seg.length / n);
    const a = [];
    for (let i = 0; i < seg.length; i += k) a.push(seg.slice(i, i + k));
    return a;
  };
  const wrap = (label) => {
    const out = [];
    let cur = '';
    label
      .split('|')
      .flatMap(chop)
      .forEach((seg) => {
        if (cur && (cur + seg).length > maxCh) {
          out.push(cur);
          cur = seg;
        } else cur += seg;
      });
    if (cur) out.push(cur);
    return out;
  };
  const lines = entries.map((e) => (horiz ? wrap(e.label) : [e.label.replace(/\|/g, '')]));
  const nLine = Math.max(...lines.map((l) => l.length));
  const vLen = Math.max(...entries.map((e) => e.label.replace(/\|/g, '').length));
  const B = (horiz ? nLine * LH + 6 : Math.min(170, vLen * FS + 10)) + 24;
  const H = horiz ? FACT_CHART_H : Math.max(FACT_CHART_H, T + 130 + B);
  const x0 = L;
  const y0 = T;
  const pw = W - L - R;
  const ph = H - T - B;
  let lo;
  let hi;
  if (series) {
    lo = Math.max(0, Math.floor((Math.min(...vals) - 2) / 10) * 10);
    hi = Math.min(100, Math.ceil((Math.max(...vals) + 2) / 10) * 10);
  } else {
    const up = Math.max(...vals) + 10;
    lo = Math.max(0, Math.floor((Math.min(...vals) - 10) / 10) * 10);
    hi = Math.min(100, (Math.floor(up) % 10 >= 5 ? Math.ceil(up / 10) : Math.floor(up / 10)) * 10);
    lo = Math.min(lo, Math.floor(Math.min(...all) / 10) * 10);
    hi = Math.max(hi, Math.ceil(Math.max(...all) / 10) * 10);
  }
  if (hi <= lo) hi = lo + 10;
  const Y = (v) => y0 + ph - ((Math.max(lo, Math.min(hi, v)) - lo) / (hi - lo)) * ph;
  const bw = Math.min(40, slot * 0.62);
  const CX = (i) => x0 + slot * (i + 0.5);
  const sorted = entries.filter((x) => x.v != null).slice().sort((a, b) => b.v - a.v);
  const top = new Set(sorted.slice(0, 3).map((x) => x.id));
  const bot = new Set(sorted.slice(-3).map((x) => x.id));
  const grid = [];
  for (let g = lo; g <= hi; g += 10) grid.push(g);
  const groupList = groups || AREAS;
  const SFS = 10;
  // 折れ線の点のスコア：ほかの点・数字となるべく重ならない側（上か下）に置く
  const seriesLabels = [];
  if (series)
    entries.forEach((it, i) => {
      const ps = series
        .map((s) => ({ s, v: s.vals[it.id] }))
        .filter((p) => p.v != null)
        .map((p) => ({ s: p.s, v: p.v, y: Y(p.v) }))
        .sort((a, b) => a.y - b.y);
      const occ = ps.map((p) => [p.y - 6, p.y + 6]);
      const hit = (r) => occ.reduce((t, [c, dd]) => t + Math.max(0, Math.min(r[1], dd) - Math.max(r[0], c)), 0);
      ps.map((p, k) => k)
        .sort((a, b) => Math.min(a, ps.length - 1 - a) - Math.min(b, ps.length - 1 - b))
        .forEach((k) => {
          const p = ps[k];
          const upR = [p.y - 17, p.y - 6];
          const dnR = [p.y + 6, p.y + 17];
          const first = k < ps.length / 2 ? upR : dnR;
          const second = first === upR ? dnR : upR;
          const use = hit(first) <= hit(second) ? first : second;
          occ.push(use);
          seriesLabels.push(
            <text
              key={`sl${i}-${k}`}
              x={CX(i)}
              y={use === upR ? p.y - 8 : p.y + 15}
              textAnchor="middle"
              fontSize={SFS}
              fontWeight="700"
              fill={p.s.color}
              stroke="var(--white)"
              strokeWidth="2.5"
              paintOrder="stroke"
              strokeLinejoin="round"
              style={TNUM}
              pointerEvents="none"
            >
              {fx1(p.v)}
            </text>
          );
        });
    });
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={aria} style={{ fontFamily: 'inherit' }}>
      {grid.map((g) => (
        <g key={g}>
          <line x1={x0} x2={x0 + pw} y1={Y(g)} y2={Y(g)} stroke="var(--grey-20)" strokeWidth="1" />
          <text x={x0 - 6} y={Y(g) + 4} textAnchor="end" fontSize={FS} fill="var(--grey-65)">
            {g}
          </text>
        </g>
      ))}
      {groupList.map((a, gi) => {
        const start = entries.findIndex((x) => x.area === a);
        if (start <= 0 || gi === 0) return null;
        const xs = x0 + slot * start;
        return <line key={`sep${a}`} x1={xs} x2={xs} y1={y0} y2={H - 2} stroke="var(--grey-30)" strokeWidth="1" />;
      })}
      {!series &&
        entries.map((it, i) => {
          if (it.v == null) return null;
          const fill = top.has(it.id) ? 'var(--green-default)' : bot.has(it.id) ? 'var(--red-default)' : 'var(--grey-30)';
          const y = Y(it.v);
          return (
            <rect key={`b${it.id}`} x={CX(i) - bw / 2} y={y} width={bw} height={Math.max(0, y0 + ph - y)} fill={fill}>
              <title>
                {`${it.name}：${fx1(it.v)}${it.g != null ? `（グループ全体 ${fx1(it.g)}）` : ''}`}
              </title>
            </rect>
          );
        })}
      <line x1={x0} x2={x0 + pw} y1={y0 + ph} y2={y0 + ph} stroke="var(--grey-45)" strokeWidth="1" />
      {groupList.map((a) => {
        const start = entries.findIndex((x) => x.area === a);
        const n = entries.filter((x) => x.area === a).length;
        if (start < 0) return null;
        const pts = entries
          .slice(start, start + n)
          .map((it, j) => (it.g == null ? null : [CX(start + j), Y(it.g)]))
          .filter(Boolean);
        return (
          <g key={`ln${a}`}>
            {pts.length > 0 && (
              <>
                <polyline
                  points={pts.map((p) => p.join(',')).join(' ')}
                  fill="none"
                  stroke="var(--black)"
                  strokeWidth="1.5"
                  strokeDasharray="5 4"
                />
                {pts.map((p, k) => (
                  <circle key={k} cx={p[0]} cy={p[1]} r="3" fill="var(--white)" stroke="var(--black)" strokeWidth="1.5" />
                ))}
              </>
            )}
            {(series || []).map((s) => {
              const lp = entries
                .slice(start, start + n)
                .map((it, j) =>
                  s.vals[it.id] == null ? null : { x: CX(start + j), y: Y(s.vals[it.id]), it, v: s.vals[it.id] }
                )
                .filter(Boolean);
              if (!lp.length) return null;
              return (
                <g key={s.name}>
                  <polyline
                    points={lp.map((p) => `${p.x},${p.y}`).join(' ')}
                    fill="none"
                    stroke={s.color}
                    strokeWidth="3"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                  {lp.map((p) => (
                    <circle key={p.it.id} cx={p.x} cy={p.y} r="4.5" fill={s.color} stroke="var(--white)" strokeWidth="1.5">
                      <title>{`${s.name}　${p.it.name}：${fx1(p.v)}`}</title>
                    </circle>
                  ))}
                </g>
              );
            })}
          </g>
        );
      })}
      {!series &&
        entries.map((it, i) => {
          if (it.v == null) return null;
          const strong = top.has(it.id) || bot.has(it.id);
          const fill = top.has(it.id) ? 'var(--green-default)' : bot.has(it.id) ? 'var(--red-default)' : 'var(--grey-30)';
          const y = Y(it.v);
          const bh = y0 + ph - y;
          const inside = bh >= (bw >= 28 ? 20 : 34);
          const ty = inside ? y + bh / 2 + 4 : y - 6;
          return (
            <text
              key={`v${it.id}`}
              x={CX(i)}
              y={ty}
              textAnchor="middle"
              fontSize={FS}
              fontWeight={strong ? 700 : 400}
              fill={inside && strong ? 'var(--white)' : 'var(--black)'}
              stroke={inside ? fill : 'var(--white)'}
              strokeWidth="3"
              paintOrder="stroke"
              strokeLinejoin="round"
              transform={inside && bw < 28 ? `rotate(-90 ${CX(i)} ${ty - 4})` : undefined}
            >
              {fx1(it.v)}
            </text>
          );
        })}
      {seriesLabels}
      {entries.map((it, i) =>
        horiz ? (
          lines[i].map((t, j) => (
            <text
              key={`n${it.id}-${j}`}
              x={CX(i)}
              y={y0 + ph + 16 + j * LH}
              textAnchor="middle"
              fontSize={FS}
              fill={t.charAt(0) === '（' ? 'var(--grey-65)' : 'var(--black)'}
            >
              {t}
            </text>
          ))
        ) : (
          <text key={`n${it.id}`} x={CX(i)} y={y0 + ph + 8} fontSize={FS} fill="var(--black)" style={{ writingMode: 'vertical-rl' }}>
            {lines[i][0]}
          </text>
        )
      )}
      {groupList.map((a) => {
        const start = entries.findIndex((x) => x.area === a);
        const n = entries.filter((x) => x.area === a).length;
        if (start < 0) return null;
        const xs = x0 + slot * start;
        return (
          <text key={`g${a}`} x={xs + (slot * n) / 2} y={H - 6} textAnchor="middle" fontSize={FS + 1.5} fontWeight="700" fill="var(--black)">
            {a}
          </text>
        );
      })}
    </svg>
  );
};

// ---- 事実の中項目 20 のエントリ（名前の括弧は 2 行目）
export const factEntries = (d, ref) =>
  FACT_CATS.map((c) => ({
    id: c.key,
    name: `${c.key} ${c.name}`,
    label: c.name.replace('（', '|（'),
    area: c.area,
    v: d.cats[c.key],
    g: ref ? ref[c.key] : null,
  }));
// ---- 共感の小項目 20 問のエントリ
export const empItemEntries = (d, refItems) =>
  CATS.filter((c) => c.kind === '共感').flatMap((c) =>
    CAT_QS[c.key].map((q) => {
      const label = EMP_ITEM_LABEL[q] || `設問${q}`;
      return { id: `q${q}`, name: label.replace(/\|/g, ''), label, area: c.area, v: d.items ? d.items[q] : null, g: refItems ? refItems[q] : null };
    })
  );
// ---- 事実の小項目 25 問（1 領域分。中項目ごとに区切る）のエントリ
export const factItemEntries = (d, area, refItems) =>
  FACT_CATS.filter((c) => c.area === area).flatMap((c) =>
    CAT_QS[c.key].map((q) => {
      const label = FACT_ITEM_NAME[q] || `設問${q}`;
      return { id: `q${q}`, name: label.replace(/\|/g, ''), label, area: c.name, v: d.items ? d.items[q] : null, g: refItems ? refItems[q] : null };
    })
  );

// ---- スコア×影響度の分布図（取り組み区分の 4 区分。帳票 p4 の優先課題のマトリクスにあたる。改修希望 E3-10 の 4）
//   size：枠の大きさ（px）。その大きさのまま描く（文字は 12px）。区分の名前は四隅、軸は「低い ―― 名前 ―→ 高い」（プロトタイプ v3.2 のとおり）。
//   区分の色：改善項目（右上）＝うすいオレンジ、強化項目（左上）＝さらにうすいオレンジ。印：改善項目＝塗り、強化項目＝白抜き、ほか＝グレー。
//   onTip を渡すと（オーバービュー）、記号にカーソルを合わせる・フォーカスしたときに呼ぶ（定義・スコア・影響度のポップアップ）
const pmRange = (d) => {
  const pts = FACT_CATS.map((c) => ({ c, s: d.cats[c.key], r: d.corr[c.key] })).filter((p) => p.s != null && p.r != null);
  if (!pts.length) return null;
  const xs = pts.map((p) => p.s).concat([d.factAvg]);
  const ys = pts.map((p) => p.r).concat([CORR_TH]);
  let x0 = Math.floor(Math.min(...xs) - 1);
  let x1 = Math.ceil(Math.max(...xs) + 1);
  if (x1 - x0 < 6) {
    const m = (x0 + x1) / 2;
    x0 = Math.floor(m - 3);
    x1 = x0 + 6;
  }
  let y0 = Math.floor((Math.min(...ys) - 0.05) * 20) / 20;
  let y1 = Math.ceil((Math.max(...ys) + 0.05) * 20) / 20;
  if (y1 - y0 < 0.3) {
    const m = (y0 + y1) / 2;
    y0 = Math.round((m - 0.15) * 20) / 20;
    y1 = y0 + 0.3;
  }
  y0 = Math.max(-1, y0);
  y1 = Math.min(1, y1);
  return { pts, x0, x1, y0, y1 };
};
export const PriorityMatrix = ({ d, size, onTip }) => {
  const rg = pmRange(d);
  if (!rg) return <p className="as-hint">{corrUnavailable(d) || '表示できるデータがありません。'}</p>;
  const W = Math.max(Math.round(size.w), 360);
  const H = Math.max(Math.round(size.h), 280);
  const FS = 12;
  const L = 66;
  const R = 20;
  const T = 10;
  const B = 52;
  const { pts, x0, x1, y0, y1 } = rg;
  const X = (v) => L + ((v - x0) / (x1 - x0)) * (W - L - R);
  const Y = (v) => H - B - ((v - y0) / (y1 - y0)) * (H - B - T);
  const xAvg = d.factAvg;
  const xstep = x1 - x0 > 12 ? 2 : 1;
  const ystep = y1 - y0 > 0.5 ? 0.1 : 0.05;
  const pxL = X(x0);
  const pxR = X(x1);
  const pyT = Y(y1);
  const pyB = Y(y0);
  const cx = X(xAvg);
  const cy = Y(CORR_TH);
  const xt = [];
  for (let g = Math.ceil(x0 / xstep) * xstep; g <= x1 + 1e-9; g += xstep) xt.push(g);
  const yt = [];
  for (let r = Math.ceil(y0 / ystep - 1e-9) * ystep; r <= y1 + 1e-9; r += ystep) yt.push(Math.round(r * 100) / 100);
  // 記号の重なりをほどく：互いに押し合い、元の位置へ引き戻す（最大 200 回）
  const RAD = 12;
  const lab = pts.map((p) => ({ p, ax: X(p.s), ay: Y(p.r), x: X(p.s), y: Y(p.r), k: fqOf(p.s, p.r, xAvg) }));
  for (let it = 0; it < 200; it += 1) {
    let moved = false;
    for (let i = 0; i < lab.length; i += 1)
      for (let j = i + 1; j < lab.length; j += 1) {
        const a = lab[i];
        const b = lab[j];
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let dd = Math.hypot(dx, dy);
        if (dd < RAD * 2 + 2) {
          if (dd < 0.01) {
            dx = Math.cos(i + j);
            dy = Math.sin(i + j);
            dd = 1;
          }
          const push = (RAD * 2 + 2 - dd) / 2;
          a.x -= (dx / dd) * push;
          a.y -= (dy / dd) * push;
          b.x += (dx / dd) * push;
          b.y += (dy / dd) * push;
          moved = true;
        }
      }
    lab.forEach((l) => {
      l.x += (l.ax - l.x) * 0.02;
      l.y += (l.ay - l.y) * 0.02;
      l.x = Math.min(Math.max(l.x, pxL + RAD), pxR - RAD);
      l.y = Math.min(Math.max(l.y, pyT + RAD), pyB - RAD);
    });
    if (!moved) break;
  }
  const hot = (l) => l.k === 'pri' || l.k === 'build';
  const xm = (pxL + pxR) / 2;
  const ym = (pyT + pyB) / 2;
  const by = H - 9;
  const sx = 14;
  const AC = 'var(--grey-45)';
  const end = (x, y, a, t) => (
    <text x={x} y={y} textAnchor={a} fontSize={FS} fill="var(--grey-65)">
      {t}
    </text>
  );
  const tipOf = (l) => ({
    key: l.p.c.key,
    name: l.p.c.name,
    area: l.p.c.area,
    q: FQ[l.k].name,
    def: CAT_DEF_SHORT[l.p.c.key] || l.p.c.def || '',
    s: fx1(l.p.s),
    r: l.p.r.toFixed(2),
  });
  return (
    <svg className="tq" viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label="スコア（横）と影響度（縦）による、事実の中項目20項目の分布">
      <rect x={pxL} y={pyT} width={pxR - pxL} height={pyB - pyT} fill="var(--white)" stroke="var(--grey-30)" />
      <rect x={cx} y={pyT} width={Math.max(0, pxR - cx)} height={Math.max(0, cy - pyT)} fill="var(--orange-background)" />
      <rect x={pxL} y={pyT} width={Math.max(0, cx - pxL)} height={Math.max(0, cy - pyT)} fill="var(--orange-background)" fillOpacity=".4" />
      {xt.map((g) => (
        <g key={`x${g}`}>
          <line x1={X(g)} x2={X(g)} y1={pyT} y2={pyB} stroke="var(--grey-20)" />
          <text x={X(g)} y={H - B + 15} textAnchor="middle" fontSize={FS} fill="var(--grey-45)" style={TNUM}>
            {g}
          </text>
        </g>
      ))}
      {yt.map((rr) => {
        const th = Math.abs(rr - CORR_TH) < 1e-9;
        return (
          <g key={`y${rr}`}>
            <line x1={pxL} x2={pxR} y1={Y(rr)} y2={Y(rr)} stroke="var(--grey-20)" />
            <text
              x={L - 6}
              y={Y(rr) + 4}
              textAnchor="end"
              fontSize={FS}
              fontWeight={th ? 700 : 400}
              fill={th ? 'var(--black)' : 'var(--grey-45)'}
              style={TNUM}
            >
              {rr.toFixed(2)}
            </text>
          </g>
        );
      })}
      <line x1={cx} x2={cx} y1={pyT} y2={pyB} stroke="var(--grey-65)" strokeWidth="1.2" />
      <line x1={pxL} x2={pxR} y1={cy} y2={cy} stroke="var(--grey-65)" strokeWidth="1.2" />
      <text x={cx + 6} y={pyB - 8} fontSize={FS} fontWeight="700" fill="var(--black)" stroke="var(--white)" strokeWidth="3" paintOrder="stroke" style={TNUM}>
        平均 {fx1(xAvg)}
      </text>
      {[
        ['build', pxL + 8, pyT + 19, 'start'],
        ['pri', pxR - 8, pyT + 19, 'end'],
        ['watch', pxL + 8, pyB - 9, 'start'],
        ['keep', pxR - 8, pyB - 9, 'end'],
      ].map(([k, tx, ty, an]) => (
        <text
          key={k}
          x={tx}
          y={ty}
          textAnchor={an}
          fontSize="13.5"
          fontWeight="700"
          fill={k === 'pri' || k === 'build' ? 'var(--orange-active)' : 'var(--grey-65)'}
          stroke="var(--white)"
          strokeWidth="3"
          paintOrder="stroke"
        >
          {FQ[k].name}
        </text>
      ))}
      {lab.map((l) =>
        Math.hypot(l.x - l.ax, l.y - l.ay) > RAD * 0.6 ? (
          <line key={`ln${l.p.c.key}`} x1={l.ax} y1={l.ay} x2={l.x} y2={l.y} stroke="var(--grey-45)" strokeWidth="1" />
        ) : null
      )}
      {lab.map((l) => (
        <circle key={`o${l.p.c.key}`} cx={l.ax} cy={l.ay} r="3" fill={hot(l) ? 'var(--chart-1)' : 'var(--grey-65)'} />
      ))}
      {lab.map((l) => {
        const hol = l.k === 'build';
        const label = `${l.p.c.key} ${l.p.c.name}（${FQ[l.k].name}）スコア ${fx1(l.p.s)}、影響度 ${l.p.r.toFixed(2)}`;
        return (
          <g key={`m${l.p.c.key}`}>
            <circle
              cx={l.x}
              cy={l.y}
              r={hol ? RAD - 1 : RAD}
              fill={l.k === 'pri' ? 'var(--chart-1)' : hol ? 'var(--white)' : 'var(--grey-45)'}
              stroke={hol ? 'var(--chart-1)' : 'var(--white)'}
              strokeWidth={hol ? 2 : 1.5}
              className={onTip ? 'pm-pt' : undefined}
              tabIndex={onTip ? 0 : undefined}
              role={onTip ? 'button' : undefined}
              aria-label={onTip ? label : undefined}
              onMouseEnter={onTip ? (e) => onTip(tipOf(l), e.currentTarget) : undefined}
              onMouseLeave={onTip ? () => onTip(null) : undefined}
              onFocus={onTip ? (e) => onTip(tipOf(l), e.currentTarget) : undefined}
              onBlur={onTip ? () => onTip(null) : undefined}
            >
              {!onTip && <title>{label}</title>}
            </circle>
            <text x={l.x} y={l.y + 4} textAnchor="middle" fontSize={FS} fontWeight="700" fill={hol ? 'var(--orange-active)' : 'var(--white)'} pointerEvents="none">
              {l.p.c.key}
            </text>
          </g>
        );
      })}
      {end(pxL, by, 'start', '低い')}
      {end(pxR, by, 'end', '高い')}
      <text x={xm} y={by} textAnchor="middle" fontSize="13.5" fontWeight="700" fill="var(--black)">
        スコア
      </text>
      <line x1={pxL + 32} x2={xm - 30} y1={by - 4} y2={by - 4} stroke={AC} strokeWidth="1" />
      <line x1={xm + 30} x2={pxR - 38} y1={by - 4} y2={by - 4} stroke={AC} strokeWidth="1" />
      <path d={`M${pxR - 31} ${by - 4}l-8 -4v8z`} fill={AC} />
      {end(sx, pyB, 'middle', '低い')}
      {end(sx, pyT + 10, 'middle', '高い')}
      <text x={sx} y={ym} textAnchor="middle" fontSize="13.5" fontWeight="700" fill="var(--black)" style={{ writingMode: 'vertical-rl' }}>
        影響度
      </text>
      <line x1={sx} x2={sx} y1={pyB - 18} y2={ym + 30} stroke={AC} strokeWidth="1" />
      <line x1={sx} x2={sx} y1={ym - 30} y2={pyT + 25} stroke={AC} strokeWidth="1" />
      <path d={`M${sx} ${pyT + 18}l-4 8h8z`} fill={AC} />
    </svg>
  );
};

// ---- 分布図の記号のポップアップ（オーバービュー「エンゲージメントを高めるための改善項目」。白地の小さな枠・記号の右か左に出す）
export const PmTip = ({ tip }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  useEffect(() => {
    if (!tip || !ref.current) return;
    const r = tip.el.getBoundingClientRect();
    const tw = ref.current.offsetWidth;
    const th = ref.current.offsetHeight;
    let left = r.right + 12;
    if (left + tw > window.innerWidth - 8) left = r.left - 12 - tw;
    setPos({
      left: Math.max(8, left),
      top: Math.max(8, Math.min(r.top + r.height / 2 - th / 2, window.innerHeight - th - 8)),
    });
  }, [tip]);
  if (!tip) return null;
  const p = tip.data;
  return (
    <div ref={ref} className="tr-tip pm-tip" role="status" style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: 'hidden' }}>
      <p className="tt-h">
        {p.key} {p.name}
        <span>
          {p.area}／{p.q}
        </span>
      </p>
      <p className="pm-def">
        <b>定義</b>
        {p.def}
      </p>
      <p className="tt-r">
        <span>スコア</span>
        <b>{p.s}</b>
      </p>
      <p className="tt-r">
        <span>影響度</span>
        <b>{p.r}</b>
      </p>
    </div>
  );
};

// ---- 回答者数が足りない・回答期間中・変化なし（空状態3種・パーツカタログ§33）
export const EmptyState = ({ type, n, minN, date, rate, most }) => {
  // 人数の制限が無いロール（minN ≦ 1）で回答 0 名のとき
  if (type === 'A' && minN <= 1)
    return (
      <div className="as-empty">
        <div className="as-empty-icon">
          <E3Icon name="hourglass" />
        </div>
        <p className="as-empty-title">まだ回答がありません</p>
        <p className="as-empty-desc">選んだ所属・属性の回答が集まると結果が表示されます。</p>
      </div>
    );
  if (type === 'A')
    return (
      <div className="as-empty">
        <div className="as-empty-icon">
          <E3Icon name="lock" />
        </div>
        <p className="as-empty-title">回答数が足りないため表示できません</p>
        <p className="as-empty-desc">
          {/* 文言は改修希望 E3-07 の 10（2026-10-13） */}
          個人の特定を防ぐため、回答数が{minN}名以上の場合のみ結果が表示されます。{n != null ? `（現在 ${n}名）` : ''}
        </p>
      </div>
    );
  if (type === 'B')
    return (
      <div className="as-empty">
        <div className="as-empty-icon">
          <E3Icon name="hourglass" />
        </div>
        <p className="as-empty-title">回答期間中です</p>
        <p className="as-empty-desc">
          結果は回答期間の終了後（{date || '締切後'}）に表示されます。
          {rate != null && (
            <>
              <br />
              現在の回収率 {rate}%
            </>
          )}
        </p>
      </div>
    );
  if (type === 'E')
    return (
      <div className="as-empty">
        <p className="as-empty-title">対象者がいません</p>
        <p className="as-empty-desc">選んだ所属・属性に当てはまる受講者がいません。条件を変えてください。</p>
      </div>
    );
  return (
    <div className="as-empty">
      <div className="as-empty-icon">
        <E3Icon name="check" />
      </div>
      <p className="as-empty-title">大きな変化はありませんでした</p>
      <p className="as-empty-desc">
        前回から ±2.0ポイント以上 変動した項目はありません。
        {most && (
          <>
            <br />
            最も動いた項目：{most}
          </>
        )}
      </p>
    </div>
  );
};

// ---- 推移の折れ線（改修希望 E3-12）。series: [{ name, color, main, vals: {seasonId: 値|null}, why: {seasonId: 理由} }]
//   横軸＝回答期間の最終日（点は左から詰める）、縦軸＝下端は最低−2（切り下げ）・上端は最高＋2（切り上げ）、線は 5 ごと。
//   値の無い回は線を切る。カーソルを合わせた回に縦の線を引き、線ごとのスコアの一覧（onHover）を出す
export const TREND_WHY = {
  small: (n) => `回答者が${n}名未満のため、表示していません`,
  next: (n) => `回答者は${n}名以上ですが、ほかの線から1名の値を計算できないように、表示していません`,
  none: () => 'この回答期間は、回答者がいません',
};
const endLabel = (v) => (v ? new Date(v).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit' }) : '');
export const TrendChart = ({ series, seasons, size, onHover }) => {
  const [hi, setHi] = useState(null);
  const vals = series.flatMap((s) => seasons.map((x) => s.vals[x.id])).filter((v) => v != null);
  if (!vals.length) return <p className="as-hint">表示できるデータがありません。</p>;
  const W = Math.max(Math.round(size.w), 360);
  const H = Math.max(Math.round(size.h), 220);
  const FS = 12;
  const L = 44;
  const R = 24;
  const T = 24;
  const B = 36;
  const PAD = 64;
  let lo = Math.max(0, Math.floor(Math.min(...vals) - 2));
  let hiV = Math.min(100, Math.ceil(Math.max(...vals) + 2));
  if (hiV <= lo) hiV = lo + 5;
  const pw = W - L - R;
  const ph = H - T - B;
  const step = (pw - PAD * 2) / (Math.max(seasons.length, 4) - 1);
  const X = (i) => L + PAD + step * i;
  const Y = (v) => T + ph - ((v - lo) / (hiV - lo)) * ph;
  const grid = [];
  for (let g = Math.ceil(lo / 5) * 5; g <= hiV; g += 5) grid.push(g);
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - r.left;
    let k = 0;
    seasons.forEach((_, i) => {
      if (Math.abs(X(i) - mx) < Math.abs(X(k) - mx)) k = i;
    });
    if (k !== hi) setHi(k);
    if (onHover) onHover({ k, x: r.left + X(k), y: e.clientY });
  };
  const leave = () => {
    setHi(null);
    if (onHover) onHover(null);
  };
  const drawOrder = series.slice().reverse(); // 先頭（全体）を最後に描いて、いちばん上に重ねる
  return (
    <svg className="tq tr-svg" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="スコアの推移" onMouseMove={move} onMouseLeave={leave}>
      {grid.map((g) => (
        <g key={g}>
          <line x1={L} x2={L + pw} y1={Y(g)} y2={Y(g)} stroke="var(--grey-20)" strokeWidth="1" />
          <text x={L - 6} y={Y(g) + 4} textAnchor="end" fontSize={FS} fill="var(--grey-65)" style={TNUM}>
            {g}
          </text>
        </g>
      ))}
      {seasons.map((x, i) => (
        <g key={x.id}>
          <line x1={X(i)} x2={X(i)} y1={T} y2={T + ph} stroke="var(--grey-20)" strokeWidth="1" strokeDasharray="2 4" />
          <text x={X(i)} y={H - B + 20} textAnchor="middle" fontSize="13.5" fontWeight="700" fill="var(--black)" style={TNUM}>
            {endLabel(x.endDate)}
          </text>
        </g>
      ))}
      <line x1={L} x2={L} y1={T} y2={T + ph} stroke="var(--grey-65)" strokeWidth="1" />
      <line x1={L} x2={L + pw} y1={T + ph} y2={T + ph} stroke="var(--grey-65)" strokeWidth="1" />
      {hi != null && <line x1={X(hi)} x2={X(hi)} y1={T} y2={T + ph} stroke="var(--grey-65)" strokeWidth="1" />}
      {drawOrder.map((s) => {
        let d = '';
        let pen = false;
        seasons.forEach((x, i) => {
          const v = s.vals[x.id];
          if (v == null) {
            pen = false;
            return;
          }
          d += `${pen ? 'L' : 'M'}${X(i)} ${Y(v)}`;
          pen = true;
        });
        return d ? <path key={`l${s.key}`} d={d} fill="none" stroke={s.color} strokeWidth={s.main ? 3.5 : 2.5} strokeLinejoin="round" strokeLinecap="round" /> : null;
      })}
      {drawOrder.map((s) =>
        seasons.map((x, i) =>
          s.vals[x.id] == null ? null : (
            <circle key={`p${s.key}${x.id}`} cx={X(i)} cy={Y(s.vals[x.id])} r={hi === i ? 7 : s.main ? 5.5 : 4.5} fill={s.color} stroke="var(--white)" strokeWidth={hi === i ? 2 : 1.5} />
          )
        )
      )}
    </svg>
  );
};
export { endLabel };
